import { getConfigTenant, isCorpTenant } from "@capone/common";
import { Experiment, RewardsAccount } from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import {
  AVAILABLE,
  CONTROL,
  LC_GATING_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  STAYS_STAY_TYPE_FILTER,
  STAYS_STAY_TYPE_FILTER_VARIANTS,
  boostLifestyleCollectionExperiment,
  boostPremierCollectionCustomSortExperiment,
  boostPremierCollectionExperiment,
  getExperimentDefVariant,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
} from "../../../../context/experiments";

/**
 * We show the Premium stays filter if there are more lodgings than what is shown in the top of the list,
 * and if the Premium stays is enabled with the proper boost experiment.
 */
export function shouldShowPremiumStaysFilter(
  collectionSpecificLodgingCounts: {
    all: number;
    filtered: number;
    top: number;
  },
  experiments: Array<Experiment>,
  rewardsAccount?: RewardsAccount
): boolean {
  const isPremierCollectionEnabled =
    getExperimentVariant(experiments, PREMIER_COLLECTION_EXPERIMENT) ===
    AVAILABLE;

  const legacyBoostVariant = getExperimentDefVariant(
    experiments,
    boostPremierCollectionExperiment
  );

  const isLegacyBoostForPremierCollectionEnabled = [
    "2-boost",
    "3-boost",
    "2-boost-randomized",
    "3-boost-randomized",
  ].includes(legacyBoostVariant);

  const isBoostForPremierCollectionEnabled =
    isLegacyBoostForPremierCollectionEnabled ||
    getExperimentDefVariant(
      experiments,
      boostPremierCollectionCustomSortExperiment
    ) !== CONTROL;

  const isCorporate = isCorpTenant(getConfigTenant());
  const isVxbCard =
    !!rewardsAccount &&
    rewardsAccount.productDisplayName
      .toLowerCase()
      .includes("venture x business");

  return (
    isPremierCollectionEnabled &&
    (isBoostForPremierCollectionEnabled || (isCorporate && isVxbCard)) &&
    collectionSpecificLodgingCounts.all > collectionSpecificLodgingCounts.top &&
    collectionSpecificLodgingCounts.filtered > 0
  );
}

/**
 * We show the Lifestyle Collection filter if there are more lodgings than what is shown in the top of the list,
 * and if the Lifestyle Collection is enabled with the proper boost experiment.
 * Don't show the filter if the Premium Collection is enabled.
 */
export function shouldShowLifestyleStaysFilter(
  collectionSpecificLodgingCounts: {
    all: number;
    top: number;
  },
  experiments: Array<Experiment>,
  rewardsAccount?: RewardsAccount
): boolean {
  const isPremierCollectionEnabled =
    getExperimentVariant(experiments, PREMIER_COLLECTION_EXPERIMENT) ===
    AVAILABLE;
  const isLifestyleCollectionEnabled =
    getExperimentVariant(experiments, LC_GATING_EXPERIMENT) === AVAILABLE;
  const isBoostForLifestyleCollectionEnabled =
    getExperimentDefVariant(experiments, boostLifestyleCollectionExperiment) !==
    CONTROL;

  const isCorporate = isCorpTenant(getConfigTenant());
  const isVxbCard =
    !!rewardsAccount &&
    rewardsAccount.productDisplayName
      .toLowerCase()
      .includes("venture x business");
  const isSparkMilesCard =
    !!rewardsAccount &&
    rewardsAccount.productDisplayName.toLowerCase().includes("spark miles");

  return (
    !isPremierCollectionEnabled &&
    isLifestyleCollectionEnabled &&
    (isBoostForLifestyleCollectionEnabled ||
      (isCorporate && (isVxbCard || isSparkMilesCard))) &&
    collectionSpecificLodgingCounts.all > collectionSpecificLodgingCounts.top
  );
}

export const getIsVacationRentalOnlyStayTypeSelected = (
  stayTypes: StayType[]
): boolean => {
  const stayTypesSet = new Set(stayTypes || []);
  return stayTypesSet.size === 1 && stayTypesSet.has(StayType.Homes);
};

export const getStayTypeFilterConfig = ({
  experiments,
  availabilityResultsHasHomesAndHotels,
  stayTypes,
}: {
  experiments: Experiment[];
  availabilityResultsHasHomesAndHotels: boolean;
  stayTypes: StayType[];
}) => {
  const dedicatedStayTypeFilterVariant = getExperimentVariantCustomVariants(
    experiments,
    STAYS_STAY_TYPE_FILTER,
    STAYS_STAY_TYPE_FILTER_VARIANTS
  );

  const dedicatedStayTypeFilterButtonGroupEnabled =
    dedicatedStayTypeFilterVariant === "AvailableButtonGroup" &&
    availabilityResultsHasHomesAndHotels;

  // Note: We'll be adding additional variants in the future
  const dedicatedStayTypeFilterEnabled =
    dedicatedStayTypeFilterButtonGroupEnabled;

  const showStayTypeFilterWithinAllFilters =
    !dedicatedStayTypeFilterEnabled && availabilityResultsHasHomesAndHotels;

  const isVacationRentalOnlyStayTypeSelected =
    getIsVacationRentalOnlyStayTypeSelected(stayTypes);

  const showVacationRentalSpecificFilters =
    dedicatedStayTypeFilterEnabled && isVacationRentalOnlyStayTypeSelected;

  return {
    showStayTypeFilterWithinAllFilters,
    showVacationRentalSpecificFilters,
    dedicatedStayTypeFilterEnabled,
    dedicatedStayTypeFilterButtonGroupEnabled,
  };
};
